// src/lib/demoSeedVersion.ts
// ============================================================
// DEMO SEED VERSION — stale browser state self-heals on load
//
// Every mutable demo value the presenter can change is cached in localStorage so it survives
// a refresh mid-demo. That cache is a liability across deploys: a browser that ran an older
// build keeps its old payloads, and the new build reads them back as if they were current.
// That is how a laptop ends up showing a personalization score, a plan tier or a finalist
// list that no longer matches the seed anyone else sees — with no visible clue why.
//
// The fix is a single stamped version. On load we compare the stamp in localStorage against
// DEMO_SEED_VERSION. If it is missing (state written before versioning existed) or lower
// (state written by an older build), every demo key is discarded and the stores rehydrate
// from the seed on their next read. Silent, automatic, no user action.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE BUMP RULE — bump DEMO_SEED_VERSION when EITHER is true:
//   1. You change a SEED VALUE that persisted state can shadow — the demo personalization
//      vector, the default weights, the canonical ACME profile, the
//      opportunity seed, or anything else a stale cache could override.
//   2. You change the SHAPE of anything in DEMO_STATE_KEYS — a new field, a renamed field,
//      a different unit. A shape change with no bump is the dangerous case: the old payload
//      still parses, so nothing errors, and the demo quietly runs on half-migrated state.
//
// If you are unsure, bump it. The only cost is that presenters lose in-progress demo state
// once, on their next load. The cost of NOT bumping is a demo that behaves differently on
// one machine for reasons nobody can see.
//
// Do NOT bump for pure code changes that touch no seed value and no persisted shape.
// ─────────────────────────────────────────────────────────────────────────────
//
// Lives in src/lib (not src/prototype) so the shared hooks that own these keys can read it
// without a shared → prototype import. (It used to be re-exported from
// prototype/config/demoPersonalization; that module went with Personalization Strength.)
// ============================================================

/**
 * Bump on any seed change or any deploy that alters demo-state shape. See THE BUMP RULE above.
 *
 * HISTORY
 * v1 — introduces versioning. Every browser carrying pre-versioning state (no stamp at all)
 *      purges once on its next load, which is the intended one-time heal.
 * v2 — heals BACKEND-ERA state. v1 shipped alongside the static-only switch, so a browser
 *      that loaded the app in the window between those two changes got its stale
 *      backend-derived state STAMPED as v1 — validly current, and therefore immune to the
 *      purge. Concretely: completion payloads carrying server-derived values such as
 *      profile 64 or docs 4-of-4, written while the app was still reading
 *      localhost:8080. A bump is exactly the designed remedy — v1 state is now "older",
 *      so every one of those browsers discards it and rehydrates to the 93% / 1-of-4 seed.
 * v3 — STATE SHAPE CHANGE. Two new persisted keys arrive with the real upload beats:
 *      dva_demo_uploaded_documents and dva_demo_initiatives. A browser holding v2 state has
 *      neither, and its completion store predates uploads driving the Docs tracker, so it
 *      would show a Docs number no upload produced. Purging is the clean start.
 * v4 — STATE SHAPE CHANGE. The evaluation-run flag (dva_demo_eval_run_pending) arrives, and
 *      the initiatives table now starts EMPTY rather than pre-seeded. A browser on v3 holds
 *      an initiatives payload written when a preview was shown by default, which would render
 *      as though the client had already uploaded something.
 * v5 — STATE SHAPE CHANGE. The Command Center record becomes maintained: milestone status and
 *      checklist edits persist (dva_demo_execution_record). A browser on v4 has no overlay, so
 *      this is purely additive — but the key must be registered before it exists, or the first
 *      presenter reset would leave edits behind.
 * v6 — HEALS ENTITLEMENT RESIDUE left by Enterprise mode. Entering Enterprise forces the shared
 *      tier override to Pro (dva_demo_plan_override='pro', prior tier stashed in
 *      dva_demo_prior_plan) and only the Standard/Enterprise pill restores it on exit. In a
 *      build where the enterprise layer is off, that pill does not render — so a laptop last
 *      left in Enterprise loads as Pro, silently, with no visible way back to freemium. The
 *      stale mode key itself is inert (useEnterpriseMode ignores it when the flag is off); it
 *      is the tier override it WROTE that outlives the flag. This is the v1→v2 lesson again:
 *      a stamp only protects state written after the stamp was correct. Bumping discards
 *      plan_override / prior_plan / enterprise_mode before the first render.
 * v7 — SEED VALUE CHANGE plus a state shape change. The Docs tracker now seeds at 0 of 4
 *      instead of 1 of 4 (DEMO_PERSONALIZATION.documents 25 → 0), and uploaded document
 *      records gain an optional `coverage` field carrying the evidence a dropped workbook
 *      produced. A browser on v6 holds a completion payload with docsCount 1 — a document
 *      no upload created and no record names — which would open the demo showing one more
 *      document than the coverage panel can find, and would flatten the on-camera upload
 *      beat that the zero seed exists to restore. Both halves of the bump rule apply here,
 *      which is the usual case: a seed value moved AND the shape around it changed.
 * v8 — STATE SHAPE CHANGE. Personalization Strength is gone, and with it the completion
 *      store. dva_demo_completion moves from DEMO_STATE_KEYS to RETIRED_STATE_KEYS: no
 *      code reads or writes it any more, so it is not live state, but a v7 browser still
 *      HAS it. Simply deleting it from the key list would strand the payload in storage
 *      forever, because the purge only clears keys it is told about. Retiring it clears it
 *      one last time on the next load.
 * v9 — STATE SHAPE CHANGE. Uploaded document records carry a `coverage` payload, and that
 *      payload gains `bases` (the per-measure supporting count a workbook derived from its
 *      own rows) alongside `scores`. A v8 browser holds a coverage payload with scores and
 *      no bases, which still PARSES — so the override display would silently render nothing
 *      for an already-uploaded document, and the one sentence this surface exists to produce
 *      would be missing with no error to explain it. The evidence-source prefix also moved
 *      from 'Structured inventory' to 'Internal document', so a stale payload would attribute
 *      the same file under a label no current code produces.
 *
 *      v9 also covers the survey binding corrections (four measures moved, added or removed
 *      in surveyMeasureBindings). Those tables are code and are read fresh on every render,
 *      so no persisted payload can shadow them and they need no bump of their own; the note
 *      is here so the change is findable from the version history rather than only the diff.
 * v10 — SEED VALUE CHANGE. 53 of the 74 DEMO_OPPORTUNITIES were retitled (and 7 recategorized)
 *      so every opportunity is now a genuine, unique row in the 174-item use case library —
 *      see libraryMapping.ts and BACKLOG.md. `ea_category_weights` persists a presenter's custom
 *      per-category weights keyed by category NAME; the category names themselves did not
 *      change, but which opportunities fall under each one did (e.g. "Research & Data Product
 *      Collaborations" drops from 19 opportunities to 13). A browser holding v9 custom weights
 *      would apply them to a differently-sized category mix than the one the presenter tuned
 *      them against, silently shifting the inclusion floor and ranked order. Bumping resets
 *      weights to default rather than carrying a tuning decision into a changed population.
 * v11 — STATE SHAPE CHANGE (Oct 21 enhancements). Imported initiative rows gain
 *      percentComplete, committed, ftesCommitted, matchedOpportunityId and warnings. A v10
 *      payload still parses but carries no matched id, so a committed initiative would
 *      silently fail to reach Evaluate. Also adds dva_demo_himss_assessments (D6, D7) and
 *      dva_demo_execute_hub (Execute hub sign-offs and PMO handoff).
 * v12 — STATE SHAPE CHANGE (Oct 21, approval workflow). dva_demo_execute_hub entries move from
 *      `signoffs` to per-step state (owner, due, checklist, decision, reopen), notes and an
 *      activity log; adds dva_demo_acting_as. The reader migrates old entries, but the bump
 *      keeps every presenter on one shape.
 * v13 — SEED VALUE CHANGE (Oct 21). The five non-US use cases (opp-5 LATAM Clinical Trials,
 *       opp-7, opp-12 Cross-Border Care Engine, opp-50, opp-51) left DEMO_OPPORTUNITIES (74 -> 69).
 *       A stale finalist list or Execute hub entry could still name them, so purge once.
 */
export const DEMO_SEED_VERSION = 13;

/** Where the stamp is kept. Deliberately NOT in DEMO_STATE_KEYS — it must survive a purge. */
export const DEMO_SEED_VERSION_KEY = 'dva_demo_seed_version';

/**
 * Every localStorage key holding mutable demo state, with its owning module.
 *
 * This is the single list: hooks/demoReset (the presenter reset) and the version purge below
 * both read it, so a key can never be added to one path and forgotten in the other.
 */
export const DEMO_STATE_KEYS = [
  'dva_demo_plan_override', // useDemoPlan — freemium/pro override
  'dva_demo_enterprise_mode', // useEnterpriseMode
  'dva_demo_prior_plan', // useEnterpriseMode (prior-tier stash)
  'dva_demo_enterprise_run', // useEnterpriseRun
  'dva_demo_enterprise_scenario', // scenarioModel (SAVED_SCENARIO_KEY)
  'dva_demo_uploaded_documents', // useDemoUploads — files the presenter dropped
  'dva_demo_initiatives', // useDemoUploads — parsed Existing Initiatives rows
  'dva_demo_himss_assessments', // useHimssAssessments — HIMSS D6/D7 intake
  'dva_demo_execute_hub', // useExecuteHubState — Execute hub approval workflow, notes, activity, PMO handoff
  'dva_demo_acting_as', // useActingAs — Execute hub "Acting as" persona
  'dva_demo_eval_run_pending', // useEvaluationRun — Evaluate interstitial armed flag
  'dva_demo_execution_record', // useExecutionRecordState — milestone/checklist edits
  'dva_meeting_mode', // presenter meeting-mode toggle
  'ea_finalist_ids', // EAContext — Add-to-Portfolio selection
  'ea_finalist_order', // EAContext — finalist ordering
  'ea_flagged_assumptions', // EAContext — flagged assumptions
  'ea_category_weights', // EAContext — Advanced Personalization category weights
  'ea_measure_weights_v2', // EAContext — per-measure weights
] as const;

/**
 * Keys that USED to hold demo state and no longer do.
 *
 * A retired key is not live state, so it does not belong in DEMO_STATE_KEYS. But deleting
 * it outright would strand whatever is already in a presenter's storage: the purge only
 * clears keys it is told about, so a forgotten key sits there for good. Listing it here
 * clears it on the next version bump and then it can be dropped from this list too.
 */
export const RETIRED_STATE_KEYS = [
  'dva_demo_completion', // v8 — useDemoCompletion, removed with Personalization Strength
] as const;

export interface DemoSeedVersionResult {
  /** True when stale state was found and discarded. */
  purged: boolean;
  /** The stamp that was found, or null when there was none (pre-versioning state). */
  foundVersion: number | null;
  /** Keys actually removed. */
  removedKeys: string[];
}

/**
 * Discard demo state written by an older build (or before versioning existed), then stamp the
 * current version.
 *
 * MUST run before any store reads localStorage. The stores read it in useState initialisers,
 * which run during the first render — so this is called from main.tsx before createRoot().
 * Calling it later would let a component initialise from stale state and then never re-read.
 *
 * Safe to call more than once; it is a no-op when the stamp is already current.
 */
export function ensureDemoSeedVersion(): DemoSeedVersionResult {
  const noop: DemoSeedVersionResult = { purged: false, foundVersion: null, removedKeys: [] };
  if (typeof window === 'undefined' || !window.localStorage) return noop;

  let found: number | null = null;
  try {
    const raw = localStorage.getItem(DEMO_SEED_VERSION_KEY);
    // A non-numeric or corrupt stamp counts as "no stamp" and triggers the purge.
    const parsed = raw === null ? NaN : Number(raw);
    found = Number.isFinite(parsed) ? parsed : null;
  } catch {
    return noop; // storage unavailable (private mode / blocked) — nothing to heal
  }

  if (found !== null && found >= DEMO_SEED_VERSION) {
    return { purged: false, foundVersion: found, removedKeys: [] };
  }

  const removedKeys: string[] = [];
  try {
    for (const key of [...DEMO_STATE_KEYS, ...RETIRED_STATE_KEYS]) {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        removedKeys.push(key);
      }
    }
    localStorage.setItem(DEMO_SEED_VERSION_KEY, String(DEMO_SEED_VERSION));
  } catch {
    return noop;
  }

  // Dev-only. Production stays silent — this is routine self-healing, not an incident.
  if (import.meta.env.DEV && removedKeys.length > 0) {
    console.info(
      `[demo] Seed version ${found ?? 'none'} → ${DEMO_SEED_VERSION}: discarded ${removedKeys.length} ` +
        `stale demo key(s) and rehydrated from the seed (${removedKeys.join(', ')}).`,
    );
  }

  return { purged: removedKeys.length > 0, foundVersion: found, removedKeys };
}
